export type LanguagePreference = "en" | "en_de" | "de" | "de_en"
export type ShowId = "gpt-101" | "gpt-111" | "gpt-131" | "gpt-132" | "gpt-141" | "gpt-142" | "gpt-151" | "gpt-152" | ""
import { readable, writable, derived } from "svelte/store"
import { currentAppUser } from "$lib/pocketbase"

const canonical = {
  de: "de-DE",
  en: "en-GB"
}

export const languagePreference = writable("en")

export const en = {
  register: "Register",
  language: "Language",
  help_language: "In which languages can you communicate comfortably in our commune?",
  username: "Username",
  help_username: "Enter your name or nickname for COMMUNE AI",
  email: "Email",
  framed_question: "Add a frame?",
  help_email: "Enter your email address",
  password: "Password",
  help_password: "Enter your Password. Passwords should be at least 8 characters long",
  additional_password: "Your password cannot contain special characters or spaces",
  password_too_short: "Your password is too short. Please use a minimum of 8 characters",
  email_exists: "Your email is already taken. Log in if it's you",
  username_exists: "Your username is already taken. Log in if it's you",
  username_too_long: "Your username is too long or too short. Please stick to 4 to 20 characters",
  username_no_spaces: "Your username cannot contain spaces",
  username_no_symbols: "Your username cannot contain special characters",
  newsletter: "Receive newsletter from Interrobang?",
  has_account: "Already have an account?",
  log_in: "Log in",
  show: "Show",
  help_show: "Please select the show you have a ticket for. You will be guided here after the registration",
  dates_111: "Wednesday, 1.11, 20:00 (Premiere)",
  dates_131: "Friday, 3.11, 18:00",
  dates_132: "Friday, 3.11, 21:00",
  dates_141: "Saturday, 4.11, 18:00",
  dates_142: "Saturday, 4.11, 21:00",
  dates_151: "Sunday, 5.11, 18:00",
  dates_152: "Sunday, 5.11, 21:00",
  alone: "It's just you, here",
  insert_image: "Insert image",
  waiting: "Waiting",
  empty_room: "Empty room",
  ask_sure: "Sure?",
  ask_multilang: "Multi-language?",
  cancel: "Cancel",
  confirm: "Confirm",
  import_template: "Import template",
  ask_overwrite: "Overwrite?",
  save_template: "Save Template",
  enter_access_code: "Enter access code",
  user_amount: (count: number) => `You are here with ${count} other${count > 1 ? "s" : ""}`,
  record_something: "Nehme etwas auf",
  press_start: "drücke 'Start', wenn du bereit bist",
  recording: "Aufnahme",
  time_left: "verbleibende Zeit",
  done: "Fertig.",
  check_recording: "bitte überprüfe deine Aufnahme",
  uploading: "Lade hoch...",
  only_jpeg_png_supported: "Only jpeg and png files are supported",
  not_supported_file_type: "Not a supported file type",
  file_too_big: "File is too big",
}

export const de = {
  register: "Registrierung",
  language: "Sprache",
  help_language: "In welchen Sprachen kannst du dich in unserer Kommune verständigen?",
  username: "Benutzername",
  help_username: "Gib deinen Namen oder Spitznamen für COMMUNE AI ein",
  email: "E-Mail",
  framed_question: "Bild soll gerahmt sein?",
  help_email: "Gib deine E-Mail-Adresse ein",
  password: "Passwort",
  help_password: "Gib dein Passwort ein. Passwörter sollten mindestens 8 Zeichen lang sein",
  password_too_short: "Dein Passwort ist zu kurz. Bitte benutze mindestens 8 Zeichen",
  additional_password: "Dein Passwort darf keine Sonderzeichen oder Leerzeichen enthalten",
  email_exists: "Deine E-Mail Adresse ist bereits registriert. Du kannst Dich damit einloggen",
  username_exists: "Deine Benutzername ist bereits registriert. Du kannst Dich damit einloggen",
  username_too_long: "Deine Benutzername soll zwischen 4 und 20 Zeichen lang sein",
  username_no_spaces: "Deine Benutzername darf keine Leerzeichen enthalten",
  username_no_symbols: "Deine Benutzername darf keine spezielle Charaktere enthalten",
  newsletter: "Newsletter von Interrobang erhalten?",
  has_account: "Hast du bereits ein Konto?",
  log_in: "Einloggen",
  show: "Vorstellung",
  help_show: "Bitte wähle die Vorstellung aus, für die du ein Ticket hast. Du wirst hierher weitergeleitet nach der Registrierung",
  dates_111: "Mittwoch, 1.11, 20:00 (Premiere)",
  dates_131: "Freitag, 3.11, 18:00",
  dates_132: "Freitag, 3.11, 21:00",
  dates_141: "Samstag, 4.11, 18:00",
  dates_142: "Samstag, 4.11, 21:00",
  dates_151: "Sonntag, 5.11, 18:00",
  dates_152: "Sonntag, 5.11, 21:00",
  alone: "Du bist hier alleine",
  insert_image: "Bild einfügen",
  waiting: "Waiting",
  empty_room: "Raum leeren",
  ask_sure: "Sicher?",
  ask_multilang: "Mehrsprachig?",
  cancel: "Annulieren",
  confirm: "Bestätigen",
  import_template: "Template importieren",
  ask_overwrite: "Überschreiben?",
  save_template: "Template speichern",
  enter_access_code: "Zugangscode eingeben",
  user_amount: (count: number) => count === 1 ? "Sie sind mit einer anderen Person hier." : `Du bist mit ${count} anderen Leuten hier.`,
  record_something: "Record something",
  press_start: "press 'Start' when you are ready",
  recording: "Recording",
  time_left: "time left",
  done: "Done.",
  check_recording: "please check your recording",
  uploading: "Uploading...",
  only_jpeg_png_supported: "Nur jpeg und png Dateien werden unterstützt",
  not_supported_file_type: "Dateityp wird nicht unterstützt",
  file_too_big: "Datei ist zu groß",
}

export const shows = writable([])

export const userShow = derived([currentAppUser, shows], ([$user, $shows]) => {
  if ($user?.show && $shows.length > 0) {
    if (!$user?.language_preference || $user?.language_preference.startsWith("en")) { // use english {}
      const s = $shows.find(s => s.key.includes("en") && s.key.includes($user?.show))

      if (s) {
        return s.value
      } else {
        return "/"
      }
    }

    if ($user?.language_preference.startsWith("de")) { // use deutsch
      const s = $shows.find(s => s.key.includes("de") && s.key.includes($user?.show))


      if (s) {
        return s.value
      } else {
        return "/"
      }
    }
  }

  return "/"
})

export const locale = derived(languagePreference, ($l) => $l.startsWith("en") ? "en" : "de")
export const canonicalLocale = derived(locale, ($l) => canonical[$l])

export const strings = readable({
  en,
  de,
  de_en: de,
  en_de: en
})

export const localeStrings = derived([locale, strings], ([$l, $s]) => {
  if ($l) return $s[$l]

  return $s["en"]
})
